import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FiX, FiMaximize, FiMinimize } from 'react-icons/fi';
import {
  GoogleMap,
  Polyline,
  Marker,
  useLoadScript,
} from '@react-google-maps/api';
import { CarProfile } from '@phosphor-icons/react';
import Tooltip from 'rc-tooltip';
import 'rc-tooltip/assets/bootstrap.css';
import './DrivingSession.css';
import DrivingSessionSidebar from './DrivingSideBar';
import ApiStore from '../../../api/ApiStore';
import { RingLoader } from 'react-spinners';
import { FiMaximize2, FiEye } from 'react-icons/fi';
import { StreetViewPanorama } from '@react-google-maps/api';
import { FaCamera } from 'react-icons/fa';

import drivingAudio from './driving.mp3';
import { FaPlay, FaPause, FaVolumeMute, FaVolumeUp } from 'react-icons/fa';

const DrivingSession = ({ session, onClose }) => {
  const [splitPosition, setSplitPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [coordinates, setCoordinates] = useState([]);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state

  const [duration, setDuration] = useState(0);
  const [distance, setDistance] = useState(0);
  const [averageSpeed, setAverageSpeed] = useState(0);

  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const dragLineRef = useRef(null);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);

  const [isMuted, setIsMuted] = useState(false); // Initialize as not muted
  const audioRef = useRef(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: 'AIzaSyAFXFtMtMKJ7bPu_pc8BBFClZOkeC2QL_w',
    libraries: ['places'],
  });

  const organizationName = 'Madel'; // Replace with actual organization name

  const [isStreetViewActive, setIsStreetViewActive] = useState(false);
  const mapRef = useRef(null);

  const fitBounds = () => {
    if (mapRef.current && coordinates.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      coordinates.forEach(({ lat, lng }) => bounds.extend({ lat, lng }));
      mapRef.current.fitBounds(bounds);
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of the earth in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c; // Distance in km
    return d;
  };

  const computeSessionStats = coords => {
    if (coords.length < 2) return;

    let totalDistance = 0;
    for (let i = 1; i < coords.length; i++) {
      totalDistance += calculateDistance(
        coords[i - 1].lat,
        coords[i - 1].lng,
        coords[i].lat,
        coords[i].lng
      );
    }
    const durationInHours =
      (coords[coords.length - 1].timestamp - coords[0].timestamp) / 3600;
    const averageSpeedKmH = totalDistance / durationInHours;

    setDuration(coords[coords.length - 1].timestamp - coords[0].timestamp); // Duration in seconds
    setDistance(totalDistance);
    setAverageSpeed(averageSpeedKmH);
  };

  const toggleStreetView = () => {
    setIsStreetViewActive(!isStreetViewActive);
  };

  useEffect(() => {
    if (coordinates.length > 0) {
      fitBounds();
    }
  }, [coordinates]);

  const handleCommentClick = timestamp => {
    if (videoRef.current) {
      // Add 1 second to the timestamp to align with video
      const adjustedTime = timestamp + 1;
      videoRef.current.currentTime = adjustedTime;
      const position = getCurrentPosition(adjustedTime);
      setCurrentPosition(position);
    }
  };

  const effectiveSplitPosition = isSidebarExpanded
    ? (splitPosition * 100) / (100 - 20) // Assuming sidebar width is 20% when expanded
    : splitPosition;

  const getTooltipPlacement = leftPosition => {
    if (leftPosition < 15) return 'rightTop';
    if (leftPosition > 75) return 'leftTop';
    return 'top';
  };

  useEffect(() => {
    const fetchSessionData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch video URL
        const videoUrl = await ApiStore.getStudentDrivingSessionVideo(
          session.session_id,
          organizationName
        );
        setVideoUrl(videoUrl);

        // Fetch GPS data
        const gpsData = await ApiStore.getStudentDrivingSessionGPS(
          session.session_id,
          organizationName
        );
        console.log('Received GPS data:', gpsData);

        // Adjust GPS timestamps
        const gpsStartTime = gpsData.length > 0 ? gpsData[0].Timestamp : 0;
        const coordinatesData = gpsData.map(point => ({
          lat: point.Latitude,
          lng: point.Longitude,
          timestamp: point.Timestamp - gpsStartTime, // Adjusted timestamp
        }));
        setCoordinates(coordinatesData);
        computeSessionStats(coordinatesData);

        // Fetch comments (notes)
        const notes = await ApiStore.getStudentDrivingSessionNotes(
          session.session_id,
          organizationName
        );
        setComments(
          notes.map(note => ({
            id: note.NoteID,
            timestamp: note.VideoTimestamp || 0,
            type: 'instructor',
            text: note.NoteText,
          }))
        );

        if (audioRef.current) {
          audioRef.current.volume = 0.5; // Set initial volume between 0 and 1
          audioRef.current.loop = true; // Loop the audio
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching session data:', error);
        setError('Failed to load session data. Please try again later.');
        setIsLoading(false);
      }
    };

    fetchSessionData();
  }, [session.session_id]);

  const handleMouseDown = e => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = e => {
    if (!isDragging) return;
    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const newPosition =
      ((e.clientX - containerRect.left) / containerRect.width) * 100;
    setSplitPosition(Math.max(10, Math.min(90, newPosition)));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const handleKeyDown = e => {
    if (e.key === 'ArrowLeft') {
      setSplitPosition(prev => Math.max(10, prev - 1));
    } else if (e.key === 'ArrowRight') {
      setSplitPosition(prev => Math.min(90, prev + 1));
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      setCurrentTime(currentTime);
      const position = getCurrentPosition(currentTime);
      console.log('Current time:', currentTime, 'Current position:', position);
      setCurrentPosition(position);
    }
  };

  const getCurrentPosition = currentTime => {
    // Adjust currentTime by subtracting 1 second
    const adjustedTime = Math.max(currentTime - 1, 0);

    for (let i = 0; i < coordinates.length - 1; i++) {
      if (
        adjustedTime >= coordinates[i].timestamp &&
        adjustedTime < coordinates[i + 1].timestamp
      ) {
        const t =
          (adjustedTime - coordinates[i].timestamp) /
          (coordinates[i + 1].timestamp - coordinates[i].timestamp);
        const lat =
          coordinates[i].lat +
          t * (coordinates[i + 1].lat - coordinates[i].lat);
        const lng =
          coordinates[i].lng +
          t * (coordinates[i + 1].lng - coordinates[i].lng);
        return { lat, lng };
      }
    }
    return coordinates[coordinates.length - 1];
  };

  useEffect(() => {
    let animationFrameId;

    const updatePosition = () => {
      if (videoRef.current && isPlaying) {
        const currentTime = videoRef.current.currentTime;
        const position = getCurrentPosition(currentTime);
        setCurrentPosition(position);
        animationFrameId = requestAnimationFrame(updatePosition);
      }
    };

    if (isPlaying) {
      animationFrameId = requestAnimationFrame(updatePosition);
    }

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isPlaying]);

  const handleTimelineClick = e => {
    const timelineRect = e.currentTarget.getBoundingClientRect();
    const clickPosition = e.clientX - timelineRect.left;
    const timelineWidth = timelineRect.width;
    const clickTime = (clickPosition / timelineWidth) * videoDuration;

    if (videoRef.current) {
      videoRef.current.currentTime = clickTime;
      const position = getCurrentPosition(clickTime);
      setCurrentPosition(position);
    }
  };

  const handleVideoLoadedMetadata = () => {
    if (videoRef.current) {
      setVideoDuration(videoRef.current.duration);
    }
  };

  const formatTime = seconds => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const progressPercent = (currentTime / videoDuration) * 100 || 0;

  const togglePlay = () => {
    if (isPlaying) {
      videoRef.current.pause();
      if (audioRef.current) {
        audioRef.current.pause();
      }
    } else {
      videoRef.current.play();
      if (!isMuted && audioRef.current) {
        audioRef.current.play();
      }
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (audioRef.current) {
      audioRef.current.muted = !audioRef.current.muted;
      if (!isMuted && isPlaying) {
        audioRef.current.play();
      } else if (isMuted) {
        audioRef.current.pause();
      }
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      containerRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
    setIsFullscreen(!isFullscreen);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const mapOptions = useMemo(
    () => ({
      disableDefaultUI: true,
      zoomControl: !isPlaying,
      streetViewControl: false,
      fullscreenControl: !isPlaying,
      mapTypeControl: !isPlaying,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },
        {
          featureType: 'transit',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }],
        },
        {
          featureType: 'road',
          elementType: 'labels.icon',
          stylers: [{ visibility: 'off' }],
        },
      ],
    }),
    [isPlaying]
  );

  // Add this function to compute the closest point on the polyline
  const getClosestPointOnRoute = clickLatLng => {
    if (!coordinates || coordinates.length === 0) return null;

    let closestPoint = null;
    let minDistance = Infinity;
    let closestSegmentIndex = 0;

    for (let i = 0; i < coordinates.length - 1; i++) {
      const segmentStart = coordinates[i];
      const segmentEnd = coordinates[i + 1];

      const candidate = findClosestPointOnSegment(
        segmentStart,
        segmentEnd,
        clickLatLng
      );
      const distance = haversineDistance(candidate, clickLatLng);

      if (distance < minDistance) {
        minDistance = distance;
        closestPoint = {
          lat: candidate.lat,
          lng: candidate.lng,
          timestamp:
            segmentStart.timestamp +
            (segmentEnd.timestamp - segmentStart.timestamp) * candidate.t,
        };
        closestSegmentIndex = i;
      }
    }

    return closestPoint;
  };

  // Helper function to find the closest point on a segment
  const findClosestPointOnSegment = (start, end, point) => {
    const startLatLng = new window.google.maps.LatLng(start.lat, start.lng);
    const endLatLng = new window.google.maps.LatLng(end.lat, end.lng);
    const pointLatLng = new window.google.maps.LatLng(point.lat(), point.lng());

    const projection = mapRef.current.getProjection();

    const startPoint = projection.fromLatLngToPoint(startLatLng);
    const endPoint = projection.fromLatLngToPoint(endLatLng);
    const clickPoint = projection.fromLatLngToPoint(pointLatLng);

    const dx = endPoint.x - startPoint.x;
    const dy = endPoint.y - startPoint.y;
    const lengthSquared = dx * dx + dy * dy;

    let t =
      ((clickPoint.x - startPoint.x) * dx +
        (clickPoint.y - startPoint.y) * dy) /
      lengthSquared;
    t = Math.max(0, Math.min(1, t));

    const closestPointX = startPoint.x + t * dx;
    const closestPointY = startPoint.y + t * dy;

    const closestLatLng = projection.fromPointToLatLng(
      new window.google.maps.Point(closestPointX, closestPointY)
    );

    return {
      lat: closestLatLng.lat(),
      lng: closestLatLng.lng(),
      t, // Fraction along the segment
    };
  };

  // Helper function to calculate haversine distance in meters
  const haversineDistance = (coord1, coord2) => {
    const R = 6371000; // Radius of the Earth in meters
    const lat1 = coord1.lat * (Math.PI / 180);
    const lat2 = coord2.lat() * (Math.PI / 180);
    const deltaLat = lat2 - lat1;
    const deltaLng = (coord2.lng() - coord1.lng) * (Math.PI / 180);

    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) *
        Math.cos(lat2) *
        Math.sin(deltaLng / 2) *
        Math.sin(deltaLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Handle map click event
  const handleMapClick = e => {
    const clickLatLng = e.latLng;
    const closestPoint = getClosestPointOnRoute(clickLatLng);

    if (closestPoint) {
      if (videoRef.current) {
        // Add 1 second to the timestamp to align with video
        const adjustedTime = closestPoint.timestamp + 1;
        videoRef.current.currentTime = adjustedTime;
        setCurrentTime(adjustedTime);
        setCurrentPosition({ lat: closestPoint.lat, lng: closestPoint.lng });
      }
    }
  };

  // Handle loading and error states
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <RingLoader color="#000000" size={120} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-screen bg-gray-100">
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={onClose}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors duration-300"
        >
          Back to Sessions
        </button>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen bg-gray-100 flex flex-col relative ${isSidebarExpanded ? 'pr-96' : 'pr-16'}`}
      ref={containerRef}
    >
      <div className="flex justify-between items-center mb-4 p-4 bg-white shadow-md">
        <h3 className="text-2xl font-semibold text-gray-800">
          {session.session_name}
        </h3>
        <div className="flex items-center space-x-4">
          <button
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            className="text-gray-600 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {isFullscreen ? <FiMinimize size={24} /> : <FiMaximize size={24} />}
          </button>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-gray-600 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FiX size={24} />
          </button>
        </div>
      </div>

      <div className="flex-grow flex">
        <div
          className="overflow-hidden transition-all duration-300 ease-in-out"
          style={{ width: `${effectiveSplitPosition}%` }}
        >
          {isLoaded && window.google ? (
            <div className="relative h-full">
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={currentPosition || coordinates[0] || { lat: 0, lng: 0 }}
                zoom={15}
                options={mapOptions}
                onLoad={map => {
                  mapRef.current = map;
                  fitBounds();
                }}
                onClick={handleMapClick} // Add onClick handler
              >
                <Polyline
                  path={coordinates}
                  options={{
                    strokeColor: '#4285F4',
                    strokeOpacity: 1,
                    strokeWeight: 6,
                  }}
                />
                {currentPosition && (
                  <Marker
                    position={currentPosition}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: 12,
                      fillColor: '#4285F4',
                      fillOpacity: 1,
                      strokeWeight: 2,
                      strokeColor: '#FFFFFF',
                    }}
                  />
                )}
                {isStreetViewActive && (
                  <StreetViewPanorama
                    position={currentPosition || coordinates[0]}
                    visible={true}
                  />
                )}
              </GoogleMap>
              {!isPlaying && (
                <div className="absolute top-1 right-12 z-10 space-y-2">
                  <button
                    onClick={fitBounds}
                    className="bg-white p-2 rounded-md shadow-md hover:bg-gray-100 transition-colors duration-200"
                    title="Fit to Route"
                  >
                    <FiMaximize2 size={24} />
                  </button>
                  <button
                    onClick={toggleStreetView}
                    className={`bg-white p-2 rounded-md shadow-md hover:bg-gray-100 transition-colors duration-200 ${isStreetViewActive ? 'text-blue-500' : ''}`}
                    title="Toggle Street View"
                  >
                    <FiEye size={24} />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          )}
        </div>

        <div
          ref={dragLineRef}
          className="w-1 bg-gray-300 cursor-col-resize hover:bg-blue-500 transition-colors duration-300 flex items-center justify-center"
          onMouseDown={handleMouseDown}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="separator"
          aria-valuenow={effectiveSplitPosition}
          aria-valuemin={10}
          aria-valuemax={90}
          aria-label="Resize panels"
        >
          <div className="w-2 h-10 bg-gray-400 rounded-full"></div>
        </div>

        <div
          className="overflow-hidden transition-all duration-300 ease-in-out flex items-center justify-center bg-black relative"
          style={{
            width: `${100 - effectiveSplitPosition}%`,
            maxHeight: 'calc(100vh - 180px)', // Adjust this value as needed
          }}
        >
          <video
            ref={videoRef}
            src={videoUrl}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleVideoLoadedMetadata}
            className="w-full h-auto max-w-full max-h-full object-contain cursor-pointer"
            controls={false}
            muted // Always keep the video muted
            disablePictureInPicture
            onClick={togglePlay}
            onEnded={() => {
              setIsPlaying(false);
              if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
              }
            }}
          />
          <audio ref={audioRef} src={drivingAudio} loop muted={isMuted} />

          {!isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center">
              <button
                onClick={togglePlay}
                className="bg-white bg-opacity-50 rounded-full p-4 hover:bg-opacity-75 transition-opacity duration-300"
                aria-label="Play"
              >
                <FaPlay size={24} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="h-14 bg-white shadow-inner relative flex flex-col px-10 mt-10">
        <div
          className="flex-grow relative"
          onClick={handleTimelineClick}
          style={{ cursor: 'pointer' }}
        >
          <div
            className="absolute top-0 left-0 h-full bg-blue-500"
            style={{
              width: `${progressPercent}%`,
            }}
          ></div>

          {comments.map(comment => {
            const leftPosition = Math.min(
              (comment.timestamp / videoDuration) * 100,
              99
            );
            return (
              <Tooltip
                key={comment.id}
                placement={getTooltipPlacement(leftPosition)}
                overlay={
                  <div className="p-2 bg-white rounded shadow-lg">
                    <p
                      className={`font-semibold ${comment.type === 'instructor' ? 'text-green-600' : 'text-yellow-600'}`}
                    >
                      {comment.type === 'instructor'
                        ? 'Instructor'
                        : 'Agente IA'}
                    </p>
                    <p className="text-sm text-gray-700">{comment.text}</p>
                  </div>
                }
                arrowContent={<div className="rc-tooltip-arrow-inner"></div>}
              >
                <div
                  className="absolute bottom-0 transform -translate-x-1/2 flex flex-col items-center hover:scale-110 transition-transform duration-200"
                  style={{ left: `${leftPosition}%` }}
                  onClick={e => {
                    e.stopPropagation();
                    if (videoRef.current) {
                      videoRef.current.currentTime = comment.timestamp;
                      const position = getCurrentPosition(comment.timestamp);
                      setCurrentPosition(position);
                    }
                  }}
                >
                  <div
                    className={`w-4 h-4 rounded-full ${comment.type === 'instructor' ? 'bg-green-500' : 'bg-yellow-500'} mb-1`}
                  ></div>
                  <div className="w-1 h-6 bg-gray-300"></div>
                </div>
              </Tooltip>
            );
          })}

          <div
            className="absolute top-0 transform -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${progressPercent}%` }}
          >
            <CarProfile size={48} color="#1f3575" weight="duotone" />
          </div>
        </div>

        <div className="flex justify-between items-center text-xs p-2 text-gray-700">
          <span>{formatTime(currentTime)}</span>
          <div className="flex space-x-2">
            <button
              onClick={togglePlay}
              className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600 transition-colors duration-300"
            >
              {isPlaying ? 'Pausar' : 'Reproducir'}
            </button>
            <button
              onClick={() => {
                videoRef.current.currentTime -= 10;
              }}
              className="bg-gray-200 px-3 py-1 rounded hover:bg-gray-300 transition-colors duration-300"
            >
              -10s
            </button>
            <button
              onClick={() => {
                videoRef.current.currentTime += 10;
              }}
              className="bg-gray-200 px-3 py-1 rounded hover:bg-gray-300 transition-colors duration-300"
            >
              +10s
            </button>
            {/* Mute/Unmute Driving Audio Button */}
            <button
              onClick={toggleMute}
              className="bg-gray-200 px-3 py-1 rounded hover:bg-gray-300 transition-colors duration-300 flex items-center"
              aria-label={
                isMuted ? 'Unmute driving audio' : 'Mute driving audio'
              }
              title={isMuted ? 'Unmute driving audio' : 'Mute driving audio'}
            >
              {isMuted ? <FaVolumeMute size={16} /> : <FaVolumeUp size={16} />}
            </button>
          </div>
          <span>{formatTime(videoDuration)}</span>
        </div>
      </div>

      <DrivingSessionSidebar
        isExpanded={isSidebarExpanded}
        setIsExpanded={setIsSidebarExpanded}
        comments={comments}
        currentTime={currentTime}
        onCommentClick={handleCommentClick}
        duration={duration}
        distance={distance}
        averageSpeed={averageSpeed}
      />
    </div>
  );
};

export default DrivingSession;
